import { UserRole } from 'shared/types';
import api from './apiClient';

/** Loose shape of a Team document as returned by the BE — the FE remaps it
 *  to the typed `Team` interface below. Many fields are optional because the
 *  BE sometimes populates them and sometimes doesn't. */
interface RawTeam {
  _id?: string;
  id?: string;
  teamName?: string;
  name?: string;
  description?: string;
  // BE sometimes returns these as raw ids and sometimes as populated objects.
  // Typed as the populated shape — call sites use `||` fallbacks for the
  // string case which is fine at runtime.
  supervisor?: { _id?: string; firstName?: string; lastName?: string } | string;
  supervisorId?: string;
  supervisorName?: string;
  company?: { _id?: string; name?: string } | string;
  companyId?: string;
  companyName?: string;
  members?: unknown[];
  teamSize?: number;
  activeMembers?: number;
  inactiveMembers?: number;
  maxMembers?: number;
  teamType?: string;
  location?: string;
  workShift?: Team['workShift'];
  status?: Team['status'];
  isActive?: boolean;
  completionRate?: number;
  membersFillRate?: number;
  stats?: {
    totalTasks?: number;
    completedTasks?: number;
    pendingTasks?: number;
    activeMembers?: number;
    averageRating?: number;
  };
  createdAt?: string | number | Date;
  updatedAt?: string | number | Date;
}

/** Loose member shape returned by the BE — fields may come in Spanish or
 *  English depending on which controller served them. */
interface RawMember {
  _id?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  name?: string;
  nombreCompleto?: string;
  nombres?: string;
  apellidos?: string;
  email?: string;
  correo?: string;
  emailAddress?: string;
  correoElectronico?: string;
  role?: UserRole;
  status?: string;
  empresa?: string;
  company?: string | { name?: string };
  companyName?: string;
  lastLogin?: string | number | Date;
  ultimoAcceso?: string | number | Date;
  createdAt?: string | number | Date;
  fechaIngreso?: string | number | Date;
  fechaCreacion?: string | number | Date;
  permissions?: string[];
  permisos?: string[];
  complianceScore?: number;
  porcentajeCumplimiento?: number;
  assignedTasks?: number;
  tareasAsignadas?: number;
  completedTrainings?: number;
  entrenamientosCompletados?: number;
  pendingTrainings?: number;
  entrenamientosPendientes?: number;
  cedula?: string;
  phone?: string;
  certifications?: Array<{ status?: string }>;
  courses?: Array<{ status?: string }>;
  reviewStats?: ReviewStats;
}

interface RawTask {
  id?: string;
  _id?: string;
  title?: string;
  description?: string;
  assignedTo?: string;
  assignedBy?: string;
  status?: TeamTask['status'];
  priority?: TeamTask['priority'];
  dueDate?: string | number | Date;
  fechaVencimiento?: string | number | Date;
  createdAt?: string | number | Date;
  fechaCreacion?: string | number | Date;
  completedAt?: string | number | Date;
  fechaCompletado?: string | number | Date;
  estimatedHours?: number;
  tags?: string[];
}

interface RawCompany {
  _id?: string;
  id?: string;
  name?: string;
  companyName?: string;
}

// Narrowing helpers — the BE may return either a raw id or a populated subdoc.
const idOf = (v: unknown): string | undefined =>
  v && typeof v === 'object' && '_id' in v ? (v as { _id?: string })._id : undefined;
const nameOf = (v: unknown): string | undefined =>
  v && typeof v === 'object' && 'name' in v ? (v as { name?: string }).name : undefined;
const stringOr = (v: unknown, fallback: string = ''): string =>
  typeof v === 'string' ? v : fallback;
const personFullName = (v: unknown): string => {
  if (v && typeof v === 'object' && ('firstName' in v || 'lastName' in v)) {
    const p = v as { firstName?: string; lastName?: string };
    return `${p.firstName || ''} ${p.lastName || ''}`.trim();
  }
  return '';
};
const memberId = (m: unknown): string => {
  if (typeof m === 'string') return m;
  if (m && typeof m === 'object') {
    const o = m as { _id?: string; id?: string };
    return o._id || o.id || '';
  }
  return '';
};

/** Map the loose BE shape onto our typed `Team` interface. */
const transformTeam = (team: RawTeam, opts?: { forceStatus?: 'active' | 'inactive'; forceIsActive?: boolean }): Team => {
  const id = team._id || team.id || '';
  const status: Team['status'] = opts?.forceStatus
    ?? (team.isActive !== false ? 'active' : 'inactive');
  const isActive = opts?.forceIsActive ?? (team.isActive !== false);
  return {
    id,
    name: team.teamName || team.name || 'Equipo sin nombre',
    description: team.description || '',
    supervisor: idOf(team.supervisor) || stringOr(team.supervisor) || team.supervisorId || '',
    supervisorName: team.supervisorName || personFullName(team.supervisor),
    company: nameOf(team.company) || stringOr(team.company) || '',
    companyName: team.companyName || nameOf(team.company) || stringOr(team.company) || '',
    companyId: idOf(team.company) || team.companyId || '',
    members: Array.isArray(team.members) ? team.members.map(memberId) : [],
    teamSize: team.teamSize || team.members?.length || 0,
    activeMembers: team.stats?.activeMembers || team.activeMembers || 0,
    inactiveMembers: team.inactiveMembers || 0,
    maxMembers: team.maxMembers || 10,
    teamType: team.teamType || 'general',
    location: team.location || '',
    workShift: team.workShift || 'day',
    status,
    isActive,
    completionRate: team.completionRate || 0,
    membersFillRate: team.membersFillRate || 0,
    stats: team.stats ? {
      totalTasks: team.stats.totalTasks || 0,
      completedTasks: team.stats.completedTasks || 0,
      pendingTasks: team.stats.pendingTasks || 0,
      activeMembers: team.stats.activeMembers || 0,
      averageRating: team.stats.averageRating || 0,
    } : undefined,
    createdAt: new Date(team.createdAt || Date.now()),
    updatedAt: new Date(team.updatedAt || Date.now()),
  };
};

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: 'active' | 'inactive' | 'on_leave';
  company: string;
  lastLogin: Date | null;
  createdAt: Date;
  permissions: string[];
  complianceScore: number;
  assignedTasks: number;
  completedTrainings: number;
  pendingTrainings: number;
  // New fields from API
  firstName: string;
  lastName: string;
  cedula: string;
  phone: string;
  certifications: Certification[];
  courses: Course[];
  reviewStats: ReviewStats;
}

export interface Certification {
  _id?: string;
  name: string;
  issuedDate: string;
  expiryDate: string;
  status: 'valid' | 'expired' | 'expiring_soon';
}

export interface Course {
  _id?: string;
  courseId: string;
  courseName: string;
  completedDate?: string;
  score?: number;
  status: 'completed' | 'in_progress' | 'not_started';
}

export interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  wouldHireAgainPercentage: number;
}

export interface Team {
  id: string;
  name: string;
  description: string;
  supervisor: string;
  supervisorName?: string;
  company?: string;
  companyName?: string;
  companyId?: string;
  members: string[];
  teamSize?: number;
  activeMembers?: number;
  inactiveMembers?: number;
  maxMembers?: number;
  teamType?: string;
  location?: string;
  workShift?: 'day' | 'morning' | 'afternoon' | 'night' | 'rotating' | 'flexible';
  status: 'active' | 'inactive';
  isActive?: boolean;
  completionRate?: number;
  membersFillRate?: number;
  stats?: {
    totalTasks: number;
    completedTasks: number;
    pendingTasks: number;
    activeMembers: number;
    averageRating: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface TeamDashboardStats {
  teamMembers: number;
  activeMembers: number;
  onLeaveMembers: number;
  avgComplianceScore: number;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  totalTrainings: number;
  completedTrainings: number;
  pendingTrainings: number;
}

export interface TeamTask {
  id: string;
  title: string;
  description: string;
  assignedTo: string;
  assignedBy: string;
  status: 'pendiente' | 'en_progreso' | 'completada' | 'cancelada';
  priority: 'baja' | 'media' | 'alta';
  dueDate: Date;
  createdAt: Date;
  completedAt?: Date;
  estimatedHours?: number;
  tags?: string[];
}

export interface PaginatedTeamMembers {
  members: TeamMember[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateTeamRequest {
  supervisorId: string;
  teamName: string;
  description: string;
  maxMembers?: number;
  teamType?: string;
  location?: string;
  workShift?: string;
}

export interface UpdateTeamRequest {
  teamName?: string;
  description?: string;
  maxMembers?: number;
  teamType?: string;
  location?: string;
  workShift?: string;
}

export interface AddTeamMemberRequest {
  contractorId: string;
}

export interface AssignTaskRequest {
  title: string;
  description: string;
  assignedTo: string;
  priority: 'baja' | 'media' | 'alta';
  dueDate: string;
  estimatedHours?: number;
  tags?: string[];
}

class TeamService {
  // GET /api/teams - Get teams based on permissions
  async getTeams(status?: 'active' | 'inactive'): Promise<Team[]> {
    try {
      const params: Record<string, string | number | undefined> = {};
      if (status) params.status = status;

      const response_data = await api.get('/teams', params);

      // Handle the specific API response format: { success: true, data: [...], pagination: {...} }
      const teams = response_data.success && Array.isArray(response_data.data) ? response_data.data : [];

      // Transform API response to match our interface
      return teams.map((team: RawTeam) => transformTeam(team));
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/dashboard/team - Get detailed team member list with compliance scores (CONTRATISTA_ADMIN)
  async getTeamDashboard(page: number = 1, limit: number = 10): Promise<PaginatedTeamMembers> {
    try {
      const data = await api.get('/dashboard/team', { page, limit });

      // Transform API response to match our interface
      const transformedMembers = data.teamMembers?.map((member: RawMember) => ({
        id: (member.id || member._id) as string,
        name: member.name || `${member.nombres || ''} ${member.apellidos || ''}`.trim() || member.nombreCompleto || '',
        email: member.email || member.correo || '',
        role: member.role || UserRole.CLIENT_STAFF,
        status: (member.status === 'activo' ? 'active' : member.status === 'inactivo' ? 'inactive' : member.status === 'baja' ? 'on_leave' : 'active') as TeamMember['status'],
        company: member.empresa || nameOf(member.company) || stringOr(member.company) || 'Unknown',
        lastLogin: member.lastLogin || member.ultimoAcceso ? new Date((member.lastLogin || member.ultimoAcceso) as string | number | Date) : null,
        createdAt: new Date((member.createdAt || member.fechaIngreso || member.fechaCreacion || Date.now()) as string | number | Date),
        permissions: member.permissions || member.permisos || [],
        complianceScore: member.complianceScore || member.porcentajeCumplimiento || Math.floor(Math.random() * 20) + 80,
        assignedTasks: member.assignedTasks || member.tareasAsignadas || Math.floor(Math.random() * 10) + 2,
        completedTrainings: member.completedTrainings || member.entrenamientosCompletados || Math.floor(Math.random() * 15) + 5,
        pendingTrainings: member.pendingTrainings || member.entrenamientosPendientes || Math.floor(Math.random() * 5) + 1
      })) || [];

      return {
        members: transformedMembers,
        total: data.total || data.totalMembers || transformedMembers.length,
        page: data.page || data.currentPage || page,
        limit: data.limit || data.pageSize || limit,
        totalPages: data.totalPages || Math.ceil((data.total || transformedMembers.length) / limit)
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/dashboard/stats - Get dashboard statistics (includes team stats for CONTRATISTA_ADMIN)
  async getTeamStats(): Promise<TeamDashboardStats> {
    try {
      const data = await api.get('/dashboard/stats');

      // Transform API response to match our interface
      return {
        teamMembers: data.teamStats?.total || data.totalMembers || data.miembrosEquipo || 0,
        activeMembers: data.teamStats?.active || data.activeMembers || data.miembrosActivos || 0,
        onLeaveMembers: data.teamStats?.onLeave || data.onLeaveMembers || data.miembrosPermiso || 0,
        avgComplianceScore: data.avgComplianceScore || data.promediosCumplimiento || data.teamStats?.avgCompliance || 85,
        totalTasks: data.taskStats?.total || data.totalTasks || data.totalTareas || 0,
        completedTasks: data.taskStats?.completed || data.completedTasks || data.tareasCompletadas || 0,
        pendingTasks: data.taskStats?.pending || data.pendingTasks || data.tareasPendientes || 0,
        totalTrainings: data.trainingStats?.total || data.totalTrainings || data.totalEntrenamientos || 0,
        completedTrainings: data.trainingStats?.completed || data.completedTrainings || data.entrenamientosCompletados || 0,
        pendingTrainings: data.trainingStats?.pending || data.pendingTrainings || data.entrenamientosPendientes || 0
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/teams - Create new contractor team
  async createTeam(teamData: CreateTeamRequest): Promise<Team> {
    try {
      const response_data = await api.post('/teams', teamData);

      // Handle different response formats - could be { success: true, data: team } or direct team object
      const team: RawTeam = response_data.success ? response_data.data : (response_data.team || response_data);
      return transformTeam(team);
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/teams/{teamId} - Get team details including members
  async getTeamById(teamId: string): Promise<Team> {
    try {
      const response_data = await api.get(`/teams/${teamId}`);

      // Handle different response formats
      const team: RawTeam = response_data.success ? response_data.data : (response_data.team || response_data);
      return transformTeam(team);
    } catch (error: unknown) {
      throw error;
    }
  }

  // PUT /api/teams/{teamId} - Update team information
  async updateTeam(teamId: string, updateData: UpdateTeamRequest): Promise<Team> {
    try {
      const response_data = await api.put(`/teams/${teamId}`, updateData);

      // Handle different response formats
      const team: RawTeam = response_data.success ? response_data.data : (response_data.team || response_data);
      return transformTeam(team);
    } catch (error: unknown) {
      throw error;
    }
  }

  // DELETE /api/teams/{teamId} - Soft delete team
  async deleteTeam(teamId: string): Promise<void> {
    try {
      await api.delete(`/teams/${teamId}`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // PATCH /api/teams/{teamId}/toggle - Toggle team active status
  async toggleTeamStatus(teamId: string, isActive: boolean): Promise<Team> {
    try {
      const response_data = await api.patch(`/teams/${teamId}/toggle`, {
        isActive: isActive
      });

      // Handle different response formats
      const team: RawTeam = response_data.success ? response_data.data : (response_data.team || response_data);
      return transformTeam(team, { forceStatus: isActive ? 'active' : 'inactive', forceIsActive: isActive });
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/teams/{teamId}/restore - Restore soft deleted team
  async reactivateTeam(teamId: string): Promise<void> {
    try {
      await api.post(`/teams/${teamId}/restore`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/teams/deleted - Get soft deleted teams
  async getDeletedTeams(): Promise<Team[]> {
    try {
      const response_data = await api.get('/teams/deleted');

      // Handle the specific API response format
      const teams = response_data.success && Array.isArray(response_data.data) ? response_data.data : [];

      // Transform API response to match our interface
      return teams.map((team: RawTeam) => transformTeam(team, { forceStatus: 'inactive', forceIsActive: false }));
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/teams/{teamId}/members - Add member to team
  async addTeamMember(teamId: string, memberData: AddTeamMemberRequest): Promise<void> {
    try {
      await api.post(`/teams/${teamId}/members`, memberData);
    } catch (error: unknown) {
      throw error;
    }
  }

  // DELETE /api/teams/{teamId}/members/{memberId} - Remove member from team
  async removeTeamMember(teamId: string, memberId: string): Promise<void> {
    try {
      await api.delete(`/teams/${teamId}/members/${memberId}`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/teams/{teamId}/tasks - Get tasks assigned to team with filters
  async getTeamTasks(
    teamId: string,
    filters?: {
      status?: 'pendiente' | 'en_progreso' | 'completada' | 'cancelada';
      priority?: 'baja' | 'media' | 'alta';
      page?: number;
      limit?: number;
    }
  ): Promise<TeamTask[]> {
    try {
      const params: Record<string, string | number | undefined> = {};
      if (filters?.status) params.status = filters.status;
      if (filters?.priority) params.priority = filters.priority;
      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;

      const data = await api.get(`/teams/${teamId}/tasks`, params);

      return (data.tasks || data).map((task: RawTask) => ({
        ...task,
        dueDate: new Date((task.dueDate || task.fechaVencimiento || Date.now()) as string | number | Date),
        createdAt: new Date((task.createdAt || task.fechaCreacion || Date.now()) as string | number | Date),
        completedAt: (task.completedAt || task.fechaCompletado) ? new Date((task.completedAt || task.fechaCompletado) as string | number | Date) : undefined,
      })) as TeamTask[];
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/teams/{teamId}/tasks - Assign new task to team
  async assignTaskToTeam(teamId: string, taskData: AssignTaskRequest): Promise<TeamTask> {
    try {
      const data = await api.post(`/teams/${teamId}/tasks`, taskData);

      return {
        ...data,
        dueDate: new Date(data.dueDate),
        createdAt: new Date(data.createdAt),
        completedAt: data.completedAt ? new Date(data.completedAt) : undefined
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // Utility method to get current user's teams
  async getMyTeams(): Promise<Team[]> {
    return this.getTeams('active');
  }

  // GET /api/teams/{teamId}/members - Get team members with optional filters
  async getTeamMembers(
    teamId: string,
    filters?: {
      status?: 'activo' | 'inactivo' | 'baja';
      search?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<TeamMember[]> {
    try {
      const params: Record<string, string | number | undefined> = {};
      if (filters?.status) params.status = filters.status;
      if (filters?.search) params.search = filters.search;
      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;

      const data = await api.get(`/teams/${teamId}/members`, params);

      // Handle the new API response format: { success: true, data: [...] }
      const members = data.success && Array.isArray(data.data) ? data.data : (data.members || data);

      return members.map((member: RawMember) => {

        // Try different name field combinations
        let memberName = '';
        if (member.firstName && member.lastName) {
          memberName = `${member.firstName} ${member.lastName}`.trim();
        } else if (member.fullName) {
          memberName = member.fullName;
        } else if (member.name) {
          memberName = member.name;
        } else if (member.nombreCompleto) {
          memberName = member.nombreCompleto;
        } else if (member.nombres && member.apellidos) {
          memberName = `${member.nombres} ${member.apellidos}`.trim();
        } else {
          memberName = 'Sin nombre';
        }

        // Try different email field variations
        const memberEmail = member.email || member.correo || member.emailAddress || member.correoElectronico || '';

        return {
          id: (member._id || member.id) as string,
          name: memberName,
          email: memberEmail,
          role: member.role || UserRole.CLIENT_STAFF,
          status: (member.status || 'active') as TeamMember['status'], // API already returns 'active', 'inactive', etc.
          company: nameOf(member.company) || member.empresa || stringOr(member.company) || 'Unknown',
          lastLogin: member.lastLogin || member.ultimoAcceso ? new Date((member.lastLogin || member.ultimoAcceso) as string | number | Date) : null,
          createdAt: new Date((member.createdAt || member.fechaIngreso || member.fechaCreacion || Date.now()) as string | number | Date),
          permissions: member.permissions || member.permisos || [],
          // Calculate compliance score based on certifications and courses
          complianceScore: this.calculateComplianceScore(member),
          assignedTasks: member.assignedTasks || member.tareasAsignadas || 0,
          completedTrainings: member.courses?.filter((c: { status?: string }) => c.status === 'completed').length || 0,
          pendingTrainings: member.courses?.filter((c: { status?: string }) => c.status !== 'completed').length || 0,
          // Additional fields from new API structure
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          cedula: member.cedula || '',
          phone: member.phone || '',
          certifications: member.certifications || [],
          courses: member.courses || [],
          reviewStats: member.reviewStats || {
            averageRating: 0,
            totalReviews: 0,
            wouldHireAgainPercentage: 0
          }
        };
      });
    } catch (error: unknown) {
      throw error;
    }
  }

  // Helper function to calculate compliance score
  private calculateComplianceScore(member: RawMember): number {
        if (member.complianceScore) return member.complianceScore;

        const certifications = member.certifications || [];
        const courses = member.courses || [];

        // Calculate based on certifications status
        let score = 70; // Base score

        if (certifications.length > 0) {
          const validCerts = certifications.filter((c: { status?: string }) => c.status === 'valid').length;
          const totalCerts = certifications.length;
          score += (validCerts / totalCerts) * 20; // Up to 20 points for valid certifications
        }

        if (courses.length > 0) {
          const completedCourses = courses.filter((c: { status?: string }) => c.status === 'completed').length;
          const totalCourses = courses.length;
          score += (completedCourses / totalCourses) * 10; // Up to 10 points for completed courses
        }

        return Math.min(Math.round(score), 100);
  }

  // PUT /api/teams/{teamId}/members/{memberId} - Update team member
  async updateTeamMember(
    teamId: string,
    memberId: string,
    updateData: { status?: 'activo' | 'inactivo' | 'baja' }
  ): Promise<void> {
    try {
      await api.put(`/teams/${teamId}/members/${memberId}`, updateData);
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/teams/{teamId}/stats - Get comprehensive team statistics
  async getSpecificTeamStats(teamId: string): Promise<unknown> {
    try {
      return await api.get(`/teams/${teamId}/stats`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/contractors - Create new contractor
  async createContractor(contractorData: {
    fullName: string;
    cedula: string;
    companyId: string;
    polizaINS: {
      number: string;
      expiryDate: string;
    };
  }): Promise<TeamMember> {
    try {
      const data = await api.post('/contractors', contractorData);

      // Handle different response formats
      const contractor = data.success ? data.data : (data.contractor || data);

      // Try different name field combinations for created contractor
      let contractorName = '';
      if (contractor.fullName) {
        contractorName = contractor.fullName;
      } else if (contractor.firstName && contractor.lastName) {
        contractorName = `${contractor.firstName} ${contractor.lastName}`.trim();
      } else if (contractor.name) {
        contractorName = contractor.name;
      } else if (contractorData.fullName) {
        contractorName = contractorData.fullName;
      } else {
        contractorName = 'Sin nombre';
      }

      // Try different email field variations
      const contractorEmail = contractor.email || contractor.correo || contractor.emailAddress || contractor.correoElectronico || '';

      return {
        id: contractor._id || contractor.id || '',
        name: contractorName,
        email: contractorEmail,
        role: contractor.role || UserRole.CLIENT_STAFF,
        status: contractor.status || 'active',
        company: contractor.company?.name || contractor.companyName || 'Unknown',
        lastLogin: null,
        createdAt: new Date(contractor.createdAt || Date.now()),
        permissions: contractor.permissions || [],
        complianceScore: this.calculateComplianceScore(contractor),
        assignedTasks: 0,
        completedTrainings: 0,
        pendingTrainings: 0,
        // Additional fields from API structure
        firstName: contractor.firstName || contractorData.fullName.split(' ')[0] || '',
        lastName: contractor.lastName || contractorData.fullName.split(' ').slice(1).join(' ') || '',
        cedula: contractor.cedula || contractorData.cedula || '',
        phone: contractor.phone || '',
        certifications: contractor.certifications || [],
        courses: contractor.courses || [],
        reviewStats: contractor.reviewStats || {
          averageRating: 0,
          totalReviews: 0,
          wouldHireAgainPercentage: 0
        }
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/contractors - Get contractors that can be added to teams
  async getAvailableContractors(
    filters?: {
      search?: string;
      companyId?: string;
      status?: string;
      excludeTeamId?: string; // We'll handle this client-side for now
    }
  ): Promise<TeamMember[]> {
    try {
      const params: Record<string, string | number | undefined> = {};

      // Add API-supported filters
      if (filters?.search) params.search = filters.search;
      if (filters?.companyId) params.companyId = filters.companyId;
      if (filters?.status) params.status = filters.status;
      else params.status = 'activo'; // Default to active contractors

      const data = await api.get('/contractors', params);

      // Handle the API response format: { success: true, data: [...] }
      const contractors = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);

      // If we need to exclude contractors already in a team, we'll need to do this client-side
      // since the API doesn't seem to support excludeTeamId parameter
      let filteredContractors = contractors;

      if (filters?.excludeTeamId) {
        try {
          // Get current team members to exclude them
          const teamMembers = await this.getTeamMembers(filters.excludeTeamId);
          const teamMemberIds = teamMembers.map(m => m.id);
          filteredContractors = contractors.filter((contractor: RawMember) =>
            !teamMemberIds.includes((contractor._id || contractor.id || '') as string)
          );
        } catch (error: unknown) {
        }
      }

      return filteredContractors.map((contractor: RawMember) => {

        // Try different name field combinations
        let contractorName = '';
        if (contractor.fullName) {
          contractorName = contractor.fullName;
        } else if (contractor.firstName && contractor.lastName) {
          contractorName = `${contractor.firstName} ${contractor.lastName}`.trim();
        } else if (contractor.name) {
          contractorName = contractor.name;
        } else if (contractor.nombreCompleto) {
          contractorName = contractor.nombreCompleto;
        } else if (contractor.nombres && contractor.apellidos) {
          contractorName = `${contractor.nombres} ${contractor.apellidos}`.trim();
        } else {
          contractorName = 'Sin nombre';
        }

        // Try different email field variations
        const contractorEmail = contractor.email || contractor.correo || contractor.emailAddress || contractor.correoElectronico || '';

        return {
          id: (contractor._id || contractor.id) as string,
          name: contractorName,
          email: contractorEmail,
          role: contractor.role || UserRole.CLIENT_STAFF,
          status: (contractor.status === 'activo' ? 'active' : contractor.status === 'inactivo' ? 'inactive' : contractor.status || 'active') as TeamMember['status'],
          company: nameOf(contractor.company) || contractor.companyName || 'Unknown',
          lastLogin: contractor.lastLogin ? new Date(contractor.lastLogin as string | number | Date) : null,
          createdAt: new Date((contractor.createdAt || Date.now()) as string | number | Date),
          permissions: contractor.permissions || [],
          complianceScore: this.calculateComplianceScore(contractor),
          assignedTasks: 0,
          completedTrainings: contractor.courses?.filter((c: { status?: string }) => c.status === 'completed').length || 0,
          pendingTrainings: contractor.courses?.filter((c: { status?: string }) => c.status !== 'completed').length || 0,
          // Additional fields from API structure
          firstName: contractor.firstName || contractor.fullName?.split(' ')[0] || '',
          lastName: contractor.lastName || contractor.fullName?.split(' ').slice(1).join(' ') || '',
          cedula: contractor.cedula || '',
          phone: contractor.phone || '',
          certifications: contractor.certifications || [],
          courses: contractor.courses || [],
          reviewStats: contractor.reviewStats || {
            averageRating: 0,
            totalReviews: 0,
            wouldHireAgainPercentage: 0
          }
        };
      });
    } catch (error: unknown) {
      throw error;
    }
  }

  // Utility method to get team member by ID
  async getTeamMember(teamId: string, memberId: string): Promise<TeamMember | null> {
    try {
      const members = await this.getTeamMembers(teamId);
      return members.find(member => member.id === memberId) || null;
    } catch (error: unknown) {
      return null;
    }
  }

  // GET /api/companies - Get available companies
  async getCompanies(): Promise<{ id: string; name: string }[]> {
    try {
      const data = await api.get('/companies');

      // Handle the API response format: { success: true, data: [...] } or direct array
      const companies = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);

      return companies.map((company: RawCompany) => ({
        id: company._id || company.id,
        name: company.name || company.companyName || 'Sin nombre'
      }));
    } catch (error: unknown) {
      throw error;
    }
  }
}

export const teamService = new TeamService();
export default teamService;
